<template>
  <div>
    <div
      v-for="(item, index) in (value || [])"
      :key="item.option_id || item.id || index"
      class="flex items-center gap-[8px] mb-[8px]"
    >
      <infinite-select
        :value="item"
        placeholder="Pilih Bahan Packing"
        class="flex-1 min-w-0 packing-infinite-select flex-0"
        option-label="name"
        append-to-body
        :default-value="item"
        :options="getMaterialOptions(item)"
        :has-next-page="Boolean(fetcher && fetcher.hasMore)"
        :debounce-timeout="500"
        :loading="Boolean(fetcher && (fetcher.isLoading || fetcher.loading))"
        :clearable="false"
        :reduce="reducePackingMaterial"
        :disabled="disabled"
        :calculate-position="positionDropdown"
        @on-search="handleSearchPacking"
        @on-intersect="handleIntersect"
        @input="val => handleSelectMaterial(index, val)"
      >
        <template #no-options="{ search, searching }">
          <template v-if="searching">
            Bahan packing untuk <em>'{{ search }}'</em> tidak ditemukan.
          </template>
          <em v-else>Masukan bahan packing yang valid</em>
        </template>
      </infinite-select>
      <SpinnerInput
        :value="item.layer"
        class="w-[60px] font-normal text-[#27272A] shadow-[0px_1px_0px_0px_#0000000D]"
        :disabled="disabled"
        :min="1"
        @input="val => handleLayerChange(index, val)"
      />
      <UiButton
        type="icon"
        class="!w-[32px] !h-[32px] hover:!bg-[#FEE2E2]"
        aria-label="Hapus bahan packing"
        :disabled="disabled"
        @click="removeItem(index)"
      >
        <span class="k-trash text-[#E31A1A] text-[16px]" />
      </UiButton>
    </div>
  </div>
</template>

<script>
import { SpinnerInput, Button } from '@core/components/ui'
import InfiniteSelect from '@/views/components/select/InfiniteSelect.vue'

export default {
  name: 'PackingSelection',
  components: {
    SpinnerInput,
    UiButton: Button,
    InfiniteSelect,
  },
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    fetcher: {
      type: Object,
      required: false,
      default: () => ({
        data: [],
        hasMore: false,
        isLoading: false,
      }),
    },
  },
  data() {
    return {
      packingOptions: [],
    }
  },
  computed: {
    selectedIdsSet() {
      const ids = new Set()
      const list = this.value || []
      list.forEach(item => {
        const id = item ? (item.option_id || item.id) : null
        if (id !== undefined && id !== null) {
          ids.add(String(id))
        }
      })
      return ids
    },
  },
  watch: {
    'fetcher.data': {
      handler(newData) {
        if (newData) {
          this.packingOptions = newData
        }
      },
      immediate: true,
    },
  },
  methods: {
    /**
     * Handle material selection for an item, merging with any duplicate selection and preserving layer.
     * @param {number} index
     * @param {Object} selectedMaterial
     */
    handleSelectMaterial(index, selectedMaterial) {
      if (!selectedMaterial) {
        return
      }
      const selectedId = selectedMaterial.option_id || selectedMaterial.id || null
      if (!selectedId) {
        return
      }
      const list = [...(this.value || [])]
      const currentItem = list[index] || {}
      const targetLayer = currentItem.layer || selectedMaterial.layer || 1

      const duplicateIndex = list.findIndex((item, i) => i !== index && item && String(item.option_id || item.id) === String(selectedId))
      if (duplicateIndex > -1) {
        list[duplicateIndex] = {
          ...list[duplicateIndex],
          id: selectedId,
          option_id: selectedId,
          name: selectedMaterial.name || list[duplicateIndex].name,
          layer: (list[duplicateIndex].layer || 1) + targetLayer,
        }
        list.splice(index, 1)
      } else {
        list[index] = {
          ...selectedMaterial,
          id: selectedId,
          option_id: selectedId,
          name: selectedMaterial.name,
          layer: targetLayer,
        }
      }
      this.$emit('input', list)
      this.$emit('change', list)
    },
    /**
     * Handle layer count changes for a specific packing item with sanitization.
     * @param {number} index
     * @param {number} newLayer
     */
    handleLayerChange(index, newLayer) {
      const list = [...(this.value || [])]
      if (list[index]) {
        list[index] = {
          ...list[index],
          layer: Math.max(1, parseInt(newLayer, 10) || 1),
        }
        this.$emit('input', list)
        this.$emit('change', list)
      }
    },
    /**
     * Remove a packing item at the specified index.
     * @param {number} index
     */
    removeItem(index) {
      const list = [...(this.value || [])]
      list.splice(index, 1)
      this.$emit('input', list)
      this.$emit('change', list)
    },
    /**
     * Format material options with disabled state computed via unified id/option_id in O(1).
     * @param {Object} currentPacking
     * @returns {Array<Object>}
     */
    getMaterialOptions(currentPacking) {
      const currentId = currentPacking ? String(currentPacking.option_id || currentPacking.id || '') : ''
      return (this.packingOptions || []).map(val => {
        const optionId = val ? String(val.option_id || val.id || '') : ''
        return {
          ...val,
          id: val?.id || val?.option_id,
          option_id: val?.option_id || val?.id,
          disabled: optionId !== '' && optionId !== currentId && this.selectedIdsSet.has(optionId),
        }
      })
    },
    /**
     * Search packing materials with pagination fetcher fallback.
     * @param {string} search
     */
    async handleSearchPacking(search) {
      if (!search) {
        this.packingOptions = this.fetcher?.data || []
        return
      }
      const searchResults = (this.fetcher?.data || []).filter(val => val.name?.toLowerCase().includes(search.toLowerCase()))
      if (searchResults.length === 0 && this.fetcher?.hasMore && this.fetcher?.fetchData) {
        await this.fetcher.fetchData()
        this.handleSearchPacking(search)
        return
      }
      this.packingOptions = searchResults
    },
    /**
     * Handle infinite select scroll intersection to fetch more options.
     */
    handleIntersect() {
      if (this.fetcher && this.fetcher.fetchData) {
        this.fetcher.fetchData()
      }
    },
    /**
     * Reduce selected packing material object for form representation.
     * @param {Object} val
     * @returns {{ name: string, layer: number, id: number, option_id: number }}
     */
    reducePackingMaterial(val) {
      const id = val ? (val.option_id || val.id || null) : null
      return {
        name: val?.name,
        layer: val?.layer || 1,
        id,
        option_id: id,
      }
    },
    /**
     * Calculate dropdown list position dynamically.
     * @param {HTMLElement} dropdownList
     * @param {Object} component
     * @param {{ width: string, left: string, top: string }} pos
     */
    positionDropdown(dropdownList, component, { width, left, top }) {
      const dropdownEl = dropdownList
      dropdownEl.style.width = width
      dropdownEl.style.left = left
      dropdownEl.style.top = top
    },
  },
}
</script>

<style lang="scss">
.vs__dropdown-option--disabled {
  opacity: 0.45 !important;
  cursor: not-allowed !important;
  pointer-events: none !important;
  background-color: #f5f5f5 !important;
  color: #a3a3a3 !important;
}

.packing-infinite-select {
  .vs__dropdown-toggle {
    border-color: #E5E5E5 !important;
    border-radius: 10px !important;
    padding: 0 !important;
    height: 36px !important;
    display: flex !important;
    align-items: center !important;
    box-shadow: 0px 1px 0px 0px #0000000D;
  }
  .vs__selected-options {
    padding: 0 12px !important;
    display: flex !important;
    align-items: center !important;
    height: 100% !important;
    flex-wrap: nowrap !important;
    position: relative !important;
    width: 100% !important;
    overflow: hidden !important;
  }
  .vs__selected {
    margin: 0 !important;
    padding: 0 !important;
    position: absolute !important;
    left: 12px !important;
    right: 12px !important;
    top: 50% !important;
    transform: translateY(-50%) !important;
    font-size: 14px !important;
    color: #0A0A0A !important;
    pointer-events: none !important;
    white-space: nowrap !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
    font-weight: normal;
  }
  .vs__search, .vs__search:focus {
    margin: 0 !important;
    padding: 0 !important;
    font-size: 14px !important;
    height: 100% !important;
    width: 100% !important;
    border: none !important;
    box-shadow: none !important;
    line-height: normal !important;
  }
  .vs__actions {
    padding: 0 8px 0 0 !important;
    display: flex !important;
    align-items: center !important;
  }
}
</style>
